import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Drawer from '../Dashboard/Drawer';
import Header from '../Dashboard/Header';
import Footer from '../Dashboard/Footer';
import HorizontalBar from '../Dashboard/Drawer/HorizontalBar';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import { TourProvider } from 'contexts/TourContext';
import { FeedbackProvider } from 'contexts/FeedbackContext';
import useOnlineStatus from 'hooks/useOnlineStatus';
import useAuth from 'hooks/useAuth';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// Mapping from offline routes back to therapist routes
const ONLINE_ROUTE_MAP = {
  '/offline': '/therapist/my-calendar',
  '/offline/my-calendar': '/therapist/my-calendar',
  '/offline/data-entry': '/therapist/data-collection',
  '/offline/data-collection': '/therapist/data-collection'
};

// ==============================|| OFFLINE DASHBOARD LAYOUT ||============================== //
// This layout mirrors DashboardLayout but without AuthGuard for offline mode

export default function OfflineDashboardLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { isOnline } = useOnlineStatus();
  const { isLoggedIn } = useAuth();
  const wasOfflineRef = useRef(!isOnline);

  const { container, miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // Auto-redirect back to therapist routes when internet is restored.
  // Only redirect if:
  // 1. User has a valid auth session (isLoggedIn) — PIN-only users stay on offline routes
  // 2. User is on a top-level offline page (calendar/data-entry hub) — NOT during active data collection
  //    Deep routes like /offline/data-collection/:childId/:type indicate active work and must not be interrupted
  useEffect(() => {
    if (wasOfflineRef.current && isOnline && isLoggedIn) {
      // Only redirect from exact top-level offline routes, not nested data collection pages
      const onlineRoute = ONLINE_ROUTE_MAP[pathname];

      if (onlineRoute) {
        navigate(onlineRoute, { replace: true });
      }
    }
    wasOfflineRef.current = !isOnline;
  }, [isOnline, pathname, navigate, isLoggedIn]);

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  // scroll to top on route change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname]);

  if (menuMasterLoading) return <Loader />;

  return (
    <FeedbackProvider>
      <TourProvider>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Header />
          {!isHorizontal ? <Drawer /> : <HorizontalBar />}

          {/* Gutters live on main only — kept in step with the online Dashboard
              layout so the two do not drift apart. See the note there. */}
          <Box component="main" sx={{ width: 'calc(100% - 260px)', flexGrow: 1, py: { xs: 2, sm: 3 }, px: { xs: 1.25, sm: 2.5 } }}>
            <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
            <Container
              maxWidth={container ? 'xl' : false}
              disableGutters
              sx={{
                position: 'relative',
                minHeight: 'calc(100vh - 110px)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Breadcrumbs />
              <Outlet />
              <Footer />
            </Container>
          </Box>
        </Box>
      </TourProvider>
    </FeedbackProvider>
  );
}
